import { Skeleton } from "@/components/atoms";
import { Button } from "@/components/atoms/Button";
import { Card } from "@/components/atoms/Card";
import { ChevronRightIcon } from "@/components/atoms/icons/ChevronRightIcon";
import { CircleCheckIcon } from "@/components/atoms/icons/CircleCheckIcon";
import { CircleIcon } from "@/components/atoms/icons/CircleIcon";
import { Typography } from "@/components/atoms/Typography";
import { LogoHeader } from "@/components/molecules/header/LogoHeader";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { useAppStore } from "@/core/stores/appStore";
import { router } from "expo-router";
import React from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { useServiceRequests } from "../hooks/useServiceRequests";
import { ServiceRequestDto } from "../services/serviceRequestService";

interface ServiceRequest {
  id: string;
  category: string;
  title: string;
  status: string;
  isClosed: boolean;
  createdAt: string;
  caseNumber?: string;
  description?: string;
  unitName?: string | null;
}

interface RequestHistoryItemProps {
  request: ServiceRequest;
  onPress?: () => void;
}

function RequestHistoryItem({ request, onPress }: RequestHistoryItemProps) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const { t } = useTranslation();

  const isResolved = request.isClosed;

  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
      <Card
        variant="default"
        padding={16}
        style={[
          styles.historyItem,
          {
            backgroundColor: colors.white,
            borderColor: colors.outlineInactive,
            borderRadius: 16,
            borderWidth: 1,
            // Remove shadow for flat card design
            shadowColor: "transparent",
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0,
            shadowRadius: 0,
            elevation: 0,
          },
        ]}
      >
        <View style={styles.historyItemContent}>
          <View style={styles.historyItemText}>
            <Typography variant="footnote" color="tertiary">
              {request.category}
            </Typography>
            <Typography variant="headline" color="primary">
              {request.title}
            </Typography>
            <View style={styles.statusContainer}>
              {isResolved ? (
                <CircleCheckIcon size={12} color={colors.fontSuccessText} />
              ) : (
                <CircleIcon size={12} color={colors.fontTertiary} />
              )}
              <Typography
                variant="footnote"
                style={[
                  {
                    color: isResolved
                      ? colors.fontSuccessText
                      : colors.fontTertiary,
                  },
                ]}
              >
                {request.status || t("support.inProgress")}
              </Typography>
            </View>
          </View>
          <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
            <ChevronRightIcon size={24} color={colors.fontPrimary} />
          </View>
        </View>
      </Card>
    </TouchableOpacity>
  );
}

export function ServiceRequestScreenMobile() {
  useCatalogDynatraceView(DT_SCREEN.SERVICE_REQUEST, 'service_request_list_viewed', true);
  const { t } = useTranslation();
  const colors = useUIColors();
  const user = useAppStore((state) => state.auth.user);
  const { data, isLoading, isError } = useServiceRequests();

  // Get display name
  const displayName =
    user?.full_name || user?.firstnameEN || user?.name || "there";

  const handleRaiseRequest = () => {
    logger.debug("ServiceRequestScreen", "Raise request pressed");
    try {
      router.push("/service-request-create");
    } catch (e) {
      logger.warn(
        "ServiceRequestScreen",
        "Navigation to create request failed",
        e,
      );
    }
  };

  const handleRequestPress = (request: ServiceRequest) => {
    logger.debug("ServiceRequestScreen", "Request pressed", {
      requestId: request.id,
    });
    trackCatalogEvent({
      screen: DT_SCREEN.SERVICE_REQUEST,
      actionName: 'service_request_open_detail',
      eventType: 'action',
      properties: { requestId: request.id },
    });
    try {
      router.push({
        pathname: "/service-request/[id]",
        params: {
          id: request.id,
          title: request.title,
          category: request.category,
          status: request.status,
          createdAt: request.createdAt,
          caseNumber: request.caseNumber ?? "",
          description: request.description ?? "",
          unit: request.unitName ?? "",
        },
      } as never);
    } catch (e) {
      logger.warn("ServiceRequestScreen", "Navigation failed", e);
    }
  };

  const mappedRequests: ServiceRequest[] =
    data?.map((item: ServiceRequestDto) => ({
      id: item.Id,
      category: item.subject || item.caseNumber || "",
      title:
        item.description || item.subject || item.caseNumber || "",
      status: item.status || item.substatus || t("support.inProgress"),
      isClosed: (item.status || "").toLowerCase() === "closed",
      createdAt: item.created_on,
      caseNumber: item.caseNumber,
      description: item.description,
      unitName: item.unitName,
    })) || [];

  const showSkeleton = isLoading && !mappedRequests.length;

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: colors.backgroundLevel1 },
      ]}
    >
      <LogoHeader showLanguageSwitch={false} hideLogo={true} />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Header Section */}
        <View style={styles.headerSection}>
          <Typography
            variant="largeTitle"
            color="primary"
            style={styles.title}
          >
            {t("support.title")}
          </Typography>
          <Typography
            variant="body"
            color="primary"
            style={styles.subtitle}
          >
            {t("support.subtitle").replace("{{name}}", displayName)}
          </Typography>
        </View>

        {/* Raise Request Button */}
        <Button
          title={t("support.raiseRequest")}
          onPress={handleRaiseRequest}
          variant="primary"
          fullWidth
          style={styles.raiseRequestButton}
        />

        {/* History Section */}
        <View style={styles.historySection}>
          <Typography
            variant="title3"
            color="primary"
            style={styles.historyTitle}
          >
            {t("support.history")}
          </Typography>

          <View style={styles.historyList}>
            {showSkeleton
              ? [...Array(3)].map((_, idx) => (
                  <Skeleton key={idx} height={96} borderRadius={16} />
                ))
              : mappedRequests.map((request) => (
                  <RequestHistoryItem
                    key={request.id}
                    request={request}
                    onPress={() => handleRequestPress(request)}
                  />
                ))}
            {!showSkeleton && !mappedRequests.length && !isLoading && (
              <View style={styles.emptyState}>
                <Typography variant="body" color="tertiary">
                  {isError ? t("common.error") : t("support.emptyState")}
                </Typography>
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 32,
    paddingBottom: 32,
    gap: 32,
  },
  headerSection: {
    gap: 16,
  },
  title: {
    fontSize: 28,
    lineHeight: 40,
    fontWeight: "500",
  },
  subtitle: {
    fontSize: 16,
    fontWeight: "400",
  },
  raiseRequestButton: {
    marginTop: 0,
  },
  historySection: {
    gap: 16,
  },
  historyTitle: {
    fontSize: 20,
    lineHeight: 28,
    fontWeight: "500",
  },
  historyList: {
    gap: 16,
  },
  emptyState: {
    paddingVertical: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  historyItem: {
    // Card styling is handled inline for white background and 16px radius
  },
  historyItemContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  historyItemText: {
    flex: 1,
    gap: 4,
    minWidth: 0, // Allow text to shrink
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
});

export default ServiceRequestScreenMobile;

